import React, { FC, HTMLAttributes, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { AutocompleteInputChangeReason, AutocompleteOwnerState, AutocompleteRenderOptionState } from '@mui/material';
import { PaperProps } from '@mui/material/Paper';
import { PopperPlacementType } from '@mui/material/Popper';
import { useTheme, styled } from '@mui/material/styles';
import { TextFieldProps } from '@mui/material/TextField';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import debounce from 'lodash/debounce';

import { IDepartmentDTO, IHierarchicalDepartmentDTO } from 'src/api/dtos';
import CommonDepartmentRepository from 'src/api/repositories/common/v1/department.repository';
import { SearchBar, SearchIcon } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { formatDepartmentDisplay } from 'src/shared/utils';

import { DepartmentSelectorPaperExtraProps } from './DepartmentSelector.types';
import { DepartmentSelectorOption } from './DepartmentSelectorOption';
import { DepartmentSelectorPaper } from './DepartmentSelectorPaper';

enum InputStateEnums {
  SEARCHING = 'searching',
  SELECTING = 'selecting',
}

interface Props {
  department?: IHierarchicalDepartmentDTO | IDepartmentDTO;
  onSelect?: (department: IHierarchicalDepartmentDTO | IDepartmentDTO) => void;
  disabled?: boolean;
  placeholder?: string;
  placement?: PopperPlacementType;
  showTooltip?: boolean;
  error?: boolean;
  isWidthMaxContent?: boolean;
  showSearchIcon?: boolean;
  sx?: TextFieldProps['sx'];
  _input?: TextFieldProps['sx'];
}

export const DepartmentSelector: FC<Props> = ({
  department,
  onSelect,
  disabled,
  placeholder,
  placement,
  showTooltip,
  error,
  isWidthMaxContent,
  showSearchIcon = true,
  sx,
  _input,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [inputState, setInputState] = useState<InputStateEnums>(InputStateEnums.SELECTING);

  const {
    data: allDepartments,
    isLoading,
    isFetching,
  } = CommonDepartmentRepository.endpoints.useGetDepartmentsQuery(
    { searchTerm: inputState === InputStateEnums.SEARCHING ? searchTerm : '' },
    {
      skip: !open,
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loading = isLoading || isFetching;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (_: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') {
      setInputState(InputStateEnums.SELECTING);
    }

    if (reason === 'input') {
      setInputState(InputStateEnums.SEARCHING);
    }

    debounceSearchTermChange(value);
    setInputValue(value);
  };

  const handleSelect = (department: IDepartmentDTO | IHierarchicalDepartmentDTO) => {
    onSelect?.(department);
    handleClose();
  };

  const renderDepartmentOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: IHierarchicalDepartmentDTO,
    state: AutocompleteRenderOptionState,
    ownerState: AutocompleteOwnerState<IHierarchicalDepartmentDTO, boolean, boolean, false, 'div'>,
  ) => {
    return (
      <StyledTreeItem
        key={option.id}
        nodeId={option.id.toString()}
        label={<DepartmentSelectorOption department={option} />}
      >
        {option.children?.length
          ? option.children.map((child) => renderDepartmentOption(props, child, state, ownerState))
          : null}
      </StyledTreeItem>
    );
  };

  const hierarchicalDepartment = useMemo<IHierarchicalDepartmentDTO | null>(
    () => (department ? { ...department, children: null } : null),
    [department],
  );

  return (
    <SearchBar
      // Open
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      // Selected
      value={hierarchicalDepartment}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      // Options
      options={allDepartments}
      getOptionLabel={(option) => formatDepartmentDisplay(option.description, option.number)}
      filterOptions={(options) => options} // Don't filter options with inputValue
      renderOption={renderDepartmentOption}
      // Loading
      loading={loading}
      // Input
      inputValue={inputValue}
      onInputChange={handleInputValueChange}
      // Paper
      PaperComponent={DepartmentSelectorPaper}
      // Props
      InputProps={{
        startAdornment: showSearchIcon && <SearchIcon color={theme.palette.action.active} />,
      }}
      slotProps={{
        paper: {
          onMouseDown: (event) => {
            event.stopPropagation();
            event.preventDefault();
          },
          departments: allDepartments,
          onDepartmentChange: handleSelect,
        } as PaperProps & DepartmentSelectorPaperExtraProps,
        popper: {
          placement: placement,
          ...(isWidthMaxContent && allDepartments.length ? { sx: { width: 'max-content !important' } } : {}),
        },
      }}
      // Settings
      disabled={disabled}
      placeholder={placeholder ?? t('employees.searchDepartments')}
      inputTooltip={
        showTooltip ? department && formatDepartmentDisplay(department.description, department.number) : undefined
      }
      error={error}
      sx={sx}
      _input={_input}
      disableClearable
    />
  );
};

const StyledTreeItem = styled(TreeItem)(() => ({
  '.MuiTreeItem-content': {
    flexDirection: 'row-reverse',
    padding: '0 16px',
    height: 36,

    '.MuiTreeItem-iconContainer': {
      margin: 0,
      alignItems: 'center',
      height: 36,
      width: 36,
      padding: 0,
    },

    '.MuiTreeItem-label': {
      padding: 0,
      fontSize: 14,
    },
  },
}));
